import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { LadingNote, LadingResponse } from '../../work-orders/work-order-details/work-order.interfaces';

export interface DropTrailerNotesModalData {
  lading_uuid: string;
  rowData: Record<string, any>;
  title?: string;
}

@Component({
  selector: 'app-drop-trailer-notes-modal',
  templateUrl: './drop-trailer-notes-modal.component.html',
  styleUrls: ['../../modals/lading-notes-modal/lading-notes-modal.component.scss'],
})
export class DropTrailerNotesModalComponent implements OnInit {
  @ViewChildren('noteTextarea') noteTextareas!: QueryList<ElementRef<HTMLTextAreaElement>>;

  loading = true;
  saving = false;
  notes: LadingNote[] = [];
  private shouldRefreshGrid = false;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DropTrailerNotesModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: DropTrailerNotesModalData,
  ) {}

  ngOnInit(): void {
    this.loadNotes();
  }

  get dialogTitle(): string {
    return this.data.title || 'Notes';
  }

  get entryCountLabel(): string {
    const count = this.notes.length;
    return `${count} ${count === 1 ? 'entry' : 'entries'}`;
  }

  get referenceLabel(): string {
    return this.data.rowData?.['alternate_reference']
      || this.data.rowData?.['v_ladings.alternate_reference']
      || this.data.lading_uuid;
  }

  close(): void {
    this.dialogRef.close({ refreshGrid: this.shouldRefreshGrid });
  }

  addNote(): void {
    this.notes.unshift({ note: '', contact_uuid: '' });

    setTimeout(() => {
      const textarea = this.noteTextareas?.first?.nativeElement;
      if (!textarea) return;
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      textarea.focus();
    });
  }

  deleteNote(index: number): void {
    const note = this.notes[index];
    if (!note) return;

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Confirm Deletion', message: 'Delete this note?' },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      if (note.lading_note_uuid) {
        this.deleteSavedNote(note, index);
        return;
      }

      this.notes.splice(index, 1);
    });
  }

  saveNotes(): void {
    if (this.saving) return;

    const payload = {
      lading_uuid: this.data.lading_uuid,
      notes: this.notes
        .map((note) => this.normalizeNote(note))
        .filter((note) => !!note.lading_note_uuid || note.note.trim().length > 0)
        .map((note) => ({
          note: note.note,
          lading_note_uuid: note.lading_note_uuid || undefined,
          contact_uuid: note.contact_uuid || undefined,
        })),
    };

    this.saving = true;

    this.http.post<any>('/api/work_orders/details/process_lading_notes.php', payload).subscribe({
      next: (response) => {
        this.saving = false;

        if (!response?.success) {
          this.toastr.error(response?.message || 'Error saving notes.');
          return;
        }

        this.notes = this.normalizeAndSortNotes(response.lading_notes);
        this.shouldRefreshGrid = true;
        this.toastr.success('Notes saved.');
      },
      error: (error) => {
        this.saving = false;
        console.error('Error saving drop trailer notes:', error);
        this.toastr.error('Error saving notes.');
      },
    });
  }

  noteMeta(note: LadingNote): string {
    const created = this.formatTimestamp(note.date);
    const modified = this.formatTimestamp(note.date_last_modified);

    if (created && modified && modified !== created) {
      return `Created ${created} - Updated ${modified}`;
    }

    if (created) return `Created ${created}`;
    if (modified) return `Updated ${modified}`;
    return 'Unsaved note';
  }

  private loadNotes(): void {
    this.loading = true;

    this.http.get<LadingResponse>(`/api/work_orders/work_order_details.php?uuid=${this.data.lading_uuid}`).subscribe({
      next: (response) => {
        this.notes = this.normalizeAndSortNotes(response?.lading_notes);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error loading drop trailer notes:', error);
        this.toastr.error('Error loading notes.');
      },
    });
  }

  private deleteSavedNote(note: LadingNote, index: number): void {
    this.http.post<any>('/api/work_orders/details/process_lading_notes.php', {
      lading_uuid: this.data.lading_uuid,
      notes: [{ lading_note_uuid: note.lading_note_uuid, note: '' }],
    }).subscribe({
      next: (response) => {
        if (!response?.success) {
          this.toastr.error(response?.message || 'Error deleting note.');
          return;
        }

        this.notes.splice(index, 1);
        this.shouldRefreshGrid = true;
        this.toastr.success('Note deleted.');
      },
      error: (error) => {
        console.error('Error deleting note:', error);
        this.toastr.error('Error deleting note.');
      },
    });
  }

  private normalizeAndSortNotes(notes?: LadingNote[]): LadingNote[] {
    return [...(notes || [])]
      .map((note) => this.normalizeNote(note))
      .sort((a, b) => this.noteSortValue(b) - this.noteSortValue(a));
  }

  private normalizeNote(note: LadingNote): LadingNote {
    return {
      ...note,
      note: note.note ?? '',
      contact_uuid: note.contact_uuid || '',
    };
  }

  private noteSortValue(note: LadingNote): number {
    const value = note.date_last_modified || note.date || '';
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
